import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import apiServices from "../../service";

const initialState: any = {
  listItems: [],
  filteredData: {},
  destributionParties: [],
  currentSelectedDates: null,
  currentSelectedDateLabel: null,
  appliedSelectedDates: null,
  providerPagination: {
    currentPage: 1,
    totalPages: 1,
    hasMore: true,
  },
  totalRows: 0,
  loading: {
    distribution: false,
    postDistribution: false,
    stopDistribution: false,
  },
  error: {
    distribution: null,
    postDistribution: null,
    stopDistribution: null,
  },
};

export const fetchDestributionList = createAsyncThunk(
  "commission/fetchDestributionList",
  async (
    {
      userId,
      dateValue,
      partyName,
      page,
      pageSize,
    }: {
      userId: string;
      dateValue: string;
      partyName: string;
      page: number;
      pageSize: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiServices.commissionModule.distribution.getList(
        dateValue,
        partyName,
        page,
        pageSize
      );
      return {
        data: response.data.data || [],
        pagination: {
          currentPage: page,
          totalPages:
            response.data.pagination?.totalPages ||
            Math.ceil(response.data.pagination?.totalItems / pageSize) ||
            1,
          hasMore:
            page <
            (response.data.pagination?.totalPages ||
              Math.ceil(response.data.pagination?.totalItems / pageSize) ||
              1),
        },
        totalRows: response.data.pagination?.totalItems || 0,
      };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch receipt list");
    }
  }
);

export const postDistribution = createAsyncThunk(
  "commission/postDistribution",
  async ({ formData }: { formData: any }, { rejectWithValue }) => {
    try {
      const response = await apiServices.commissionModule.distribution.add(
        formData
      );
      const data = response?.data;

      if (data?.isError === false) {
        return data;
      } else {
        const errorMessage =
          data?.details?.map((d: any) => d.error).join(", ") ||
          data?.message ||
          "Something went wrong";
        return rejectWithValue(errorMessage);
      }
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to post distribution"
      );
    }
  }
);

export const postStopDistribution = createAsyncThunk<
  any,
  { commissionAllocationId: string; stopDistributionDate: string }, // argument (payload) type
  { rejectValue: string } // type of rejectWithValue
>("commission/postStopDistribution", async (payload, { rejectWithValue }) => {
  try {
    const response = await apiServices.commissionModule.distribution.stop(
      payload
    );

    if (response?.data?.isError) {
      return rejectWithValue(
        response.data?.error?.message?.[0] ||
        response.data?.message ||
        "Failed to stop commission distribution"
      );
    }

    if (response?.data) {
      return response.data;
    } else {
      return rejectWithValue("Failed to stop commission distribution");
    }
  } catch (error: any) {
    return rejectWithValue(
      error?.message || "Failed to stop commission distribution"
    );
  }
});

export const fetchDistributionDetails = createAsyncThunk(
  "commission/fetchDistributionDetails",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiServices.commissionModule.distribution.getDetail(
        id
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to fetch distribution details"
      );
    }
  }
);


export const distributionSlice = createSlice({
  name: "destribution",
  initialState,
  reducers: {
    setFilteredData: (state, action: PayloadAction<any>) => {
      state.filteredData = action.payload;
    },
    setSelectedParties: (
      state,
      action: PayloadAction<{ id: string; name: string }[]>
    ) => {
      state.selectedPartyNames = action.payload;
    },
    setCurrentSelectedDates: (
      state,
      action: PayloadAction<{ value: string | null; label: string | null }>
    ) => {
      state.currentSelectedDates = action.payload.value;
      state.currentSelectedDateLabel = action.payload.label;
    },
    setAppliedSelectedDates: (state, action: PayloadAction<string | null>) => {
      state.appliedSelectedDates = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDestributionList.pending, (state) => {
        state.loading.distribution = true;
        state.error.distribution = null;
      })
      .addCase(fetchDestributionList.fulfilled, (state, action) => {
        state.listItems = action.payload.data;
        state.loading.distribution = false;
        state.totalRows = action.payload.totalRows;
        state.providerPagination = action.payload.pagination;
      })
      .addCase(fetchDestributionList.rejected, (state, action) => {
        state.loading.distribution = false;
        state.error.distribution = action.payload as string;
      })
      .addCase(postDistribution.pending, (state) => {
        state.loading.postDistribution = true;
        state.error.postDistribution = null;
      })
      .addCase(postDistribution.fulfilled, (state) => {
        state.loading.postDistribution = false;
      })
      .addCase(postDistribution.rejected, (state, action) => {
        state.loading.postDistribution = false;
        state.error.postDistribution = action.payload as string;
      })
      .addCase(postStopDistribution.pending, (state) => {
        state.loading.stopDistribution = true;
        state.error.stopDistribution = null;
      })
      .addCase(postStopDistribution.fulfilled, (state) => {
        state.loading.stopDistribution = false;
      })
      .addCase(postStopDistribution.rejected, (state, action) => {
        state.loading.stopDistribution = false;
        state.error.stopDistribution = action.payload as string;
      })
      .addCase(fetchDistributionDetails.pending, (state) => {
        state.loading.distributionDetails = true;
        state.error.distributionDetails = null;
      })
      .addCase(fetchDistributionDetails.fulfilled, (state, action) => {
        state.loading.distributionDetails = false;
        state.distributionDetails = action.payload.data;
      })
      .addCase(fetchDistributionDetails.rejected, (state, action) => {
        state.loading.distributionDetails = false;
        state.error.distributionDetails = action.payload as string;
      });
  },
});

export const {
  setFilteredData,
  setSelectedParties,
  setCurrentSelectedDates,
  setAppliedSelectedDates,
} = distributionSlice.actions;

export default distributionSlice.reducer;
